import entities from 'entities';
import xml2js from 'xml2js';
import http from './http';


const fields = {
    feed: [
        ['author', 'creator'],
        ['dc:publisher', 'publisher'],
        ['dc:creator', 'creator'],
        ['dc:source', 'source'],
        ['dc:title', 'title'],
        ['dc:type', 'type'],
        'title',
        'description',
        'author',
        'pubDate',
        'webMaster',
        'managingEditor',
        'generator',
        'link',
        'language',
        'copyright',
        'lastBuildDate',
        'docs',
        'generator',
        'ttl',
        'rating',
        'skipHours',
        'skipDays',
    ],
    item: [
        ['author', 'creator'],
        ['dc:creator', 'creator'],
        ['dc:date', 'date'],
        ['dc:language', 'language'],
        ['dc:rights', 'rights'],
        ['dc:source', 'source'],
        ['dc:title', 'title'],
        'title',
        'link',
        'pubDate',
        'author',
        'content:encoded',
        'enclosure',
        'dc:creator',
        'dc:date',
        'comments',
    ],
    podcastFeed: [
        ['itunes:author', 'author'],
        ['itunes:subtitle', 'subtitle'],
        ['itunes:summary', 'summary'],
        ['itunes:explicit', 'explicit']
    ],
    podcastItem: [
        ['itunes:author', 'author'],
        ['itunes:subtitle', 'subtitle'],
        ['itunes:summary', 'summary'],
        ['itunes:explicit', 'explicit'],
        ['itunes:duration', 'duration'],
        ['itunes:image', 'image'],
        ['itunes:episode', 'episode'],
        ['itunes:season', 'season'],
        ['itunes:keywords', 'keywords']
    ]
};

function stripHtml(str) {
    return str.replace(/<(?:.|\n)*?>/gm, '');
}

function getSnippet(str) {
    return entities.decode(stripHtml(str)).trim();
}

function getLink(links, rel, fallbackIdx) {
    if (!links) return;
    for (let i = 0; i < links.length; ++i) {
        if (links[i].$.rel === rel) return links[i].$.href;
    }
    if (links[fallbackIdx]) return links[fallbackIdx].$.href;
}

function getContent(content) {
    if (typeof content._ === 'string') {
        return content._;
    }

    if (typeof content === 'object') {
        const builder = new xml2js.Builder({
            headless: true,
            explicitRoot: true,
            rootName: 'div',
            renderOpts: {
                pretty: false
            }
        });
        return builder.buildObject(content);
    }

    return content;
}

function copyFromXML(xml, dest, fields) {
    fields.forEach(function (f) {
        let from = f;
        let to = f;
        let options = {};
        if (Array.isArray(f)) {
            from = f[0];
            to = f[1];
            if (f.length > 2) {
                options = f[2];
            }
        }
        const { keepArray } = options;
        if (xml[from] !== undefined)
            dest[to] = keepArray ? xml[from] : xml[from][0];
    });
}

function maybePromisify(callback, promise) {
    if (!callback) return promise;
    return promise.then(
        (data) => setTimeout(() => callback(null, data)),
        (err) => setTimeout(() => callback(err))
    );
}


export class RssService {
    constructor(options = {}) {
        options.headers = options.headers || {};
        options.xml2js = options.xml2js || {};
        options.customFields = options.customFields || {};
        options.customFields.item = options.customFields.item || [];
        options.customFields.feed = options.customFields.feed || [];
        if (!options.maxRedirects) options.maxRedirects = 5;
        this.options = options;
        this.xmlParser = new xml2js.Parser(this.options.xml2js);
    }

    parseString(xml, callback) {
        let prom = new Promise((resolve, reject) => {
            this.xmlParser.parseString(xml, (err, result) => {
                if (err) return reject(err);
                if (!result) {
                    return reject(new Error('Unable to parse XML.'));
                }
                let feed = null;
                if (result.feed) {
                    feed = this.buildAtomFeed(result);
                } else if (result.rss && result.rss.$ && result.rss.$.version && result.rss.$.version.match(/^2/)) {
                    feed = this.buildRSS2(result);
                } else if (result['rdf:RDF']) {
                    feed = this.buildRSS1(result);
                } else if (result.rss && result.rss.$ && result.rss.$.version && result.rss.$.version.match(/0\.9/)) {
                    feed = this.buildRSS0_9(result);
                } else if (result.rss && this.options.defaultRSS) {
                    switch (this.options.defaultRSS) {
                        case 0.9:
                            feed = this.buildRSS0_9(result);
                            break;
                        case 1:
                            feed = this.buildRSS1(result);
                            break;
                        case 2:
                            feed = this.buildRSS2(result);
                            break;
                        default:
                            return reject(new Error("default RSS version not recognized."));
                    }
                } else {
                    return reject(new Error("Feed not recognized as RSS 1 or 2."));
                }
                resolve(feed);
            });
        });
        prom = maybePromisify(callback, prom);
        return prom;
    }

    parseURL(feedUrl, callback, redirectCount = 0) {
        let xml = '';
        let prom = new Promise((resolve, reject) => {
            const req = http.get(feedUrl, {
                responseType: 'text',
                headers: {
                    'accept': 'application/rss+xml'
                }
            });

            req.then((res) => {
                if (this.options.maxRedirects && res.status >= 300 && res.status < 400 && res.headers['location']) {
                    if (redirectCount === this.options.maxRedirects) {
                        return reject(new Error("Too many redirects"));
                    }

                    return this.parseURL(res.headers['location'], null, redirectCount + 1).then(resolve, reject);
                }
                if (res.status >= 300) {
                    return reject(new Error(`Status code ${res.status}`));
                }

                xml = res.data;

                this.parseString(xml).then(resolve, reject);
            });

            req.catch((err) => {
                reject(err);
            });
        });
        prom = maybePromisify(callback, prom);
        return prom;
    }

    buildAtomFeed(xmlObj) {
        const feed = { items: [] };
        copyFromXML(xmlObj.feed, feed, this.options.customFields.feed);
        if (xmlObj.feed.link) {
            feed.link = getLink(xmlObj.feed.link, 'alternate', 0);
            feed.feedUrl = getLink(xmlObj.feed.link, 'self', 1);
        }
        if (xmlObj.feed.title) {
            let title = xmlObj.feed.title[0] || '';
            if (title._) title = title._;
            if (title) feed.title = title;
        }
        if (xmlObj.feed.updated) {
            feed.lastBuildDate = xmlObj.feed.updated[0];
        }
        (xmlObj.feed.entry || []).forEach((entry) => {
            const item = {};
            copyFromXML(entry, item, this.options.customFields.item);
            if (entry.title) {
                let title = entry.title[0] || '';
                if (title._) title = title._;
                if (title) item.title = title;
            }
            if (entry.link && entry.link.length) {
                item.link = getLink(entry.link, 'alternate', 0);
            }
            if (entry.published && entry.published.length && entry.published[0].length) item.pubDate = new Date(entry.published[0]).toISOString();
            if (!item.pubDate && entry.updated && entry.updated.length && entry.updated[0].length) item.pubDate = new Date(entry.updated[0]).toISOString();
            if (entry.author && entry.author.length) item.author = entry.author[0].name[0];
            if (entry.content && entry.content.length) {
                item.content = getContent(entry.content[0]);
                item.contentSnippet = getSnippet(item.content);
            }
            if (entry.id) {
                item.id = entry.id[0];
            }
            this.setISODate(item);
            feed.items.push(item);
        });
        return feed;
    }

    buildRSS0_9(xmlObj) {
        var channel = xmlObj.rss.channel[0];
        var items = channel.item;
        return this.buildRSS(channel, items);
    }

    buildRSS1(xmlObj) {
        xmlObj = xmlObj['rdf:RDF'];
        const channel = xmlObj.channel[0];
        const items = xmlObj.item;
        return this.buildRSS(channel, items);
    }

    buildRSS2(xmlObj) {
        const channel = xmlObj.rss.channel[0];
        const items = channel.item;
        const feed = this.buildRSS(channel, items);
        if (xmlObj.rss.$ && xmlObj.rss.$['xmlns:itunes']) {
            this.decorateItunes(feed, channel);
        }
        return feed;
    }

    buildRSS(channel, items) {
        items = items || [];
        const feed = { items: [] };
        const feedFields = fields.feed.concat(this.options.customFields.feed);
        const itemFields = fields.item.concat(this.options.customFields.item);
        if (channel['atom:link']) feed.feedUrl = channel['atom:link'][0].$.href;
        if (channel.image && channel.image[0] && channel.image[0].url) {
            feed.image = {};
            const image = channel.image[0];
            if (image.link) feed.image.link = image.link[0];
            if (image.url) feed.image.url = image.url[0];
            if (image.title) feed.image.title = image.title[0];
            if (image.width) feed.image.width = image.width[0];
            if (image.height) feed.image.height = image.height[0];
        }
        copyFromXML(channel, feed, feedFields);
        items.forEach((xmlItem) => {
            const item = {};
            copyFromXML(xmlItem, item, itemFields);
            if (xmlItem.enclosure) {
                item.enclosure = xmlItem.enclosure[0].$;
            }
            if (xmlItem.description) {
                item.content = getContent(xmlItem.description[0]);
                item.contentSnippet = getSnippet(item.content);
            }
            if (xmlItem.guid) {
                item.guid = xmlItem.guid[0];
                if (item.guid._) item.guid = item.guid._;
            }
            if (xmlItem.category) item.categories = xmlItem.category;
            this.setISODate(item);
            feed.items.push(item);
        });
        return feed;
    }

    decorateItunes(feed, channel) {
        const items = channel.item || [],
            entry = {};
        feed.itunes = {};

        if (channel['itunes:owner']) {
            var owner = {},
                image;

            if (channel['itunes:owner'][0]['itunes:name']) {
                owner.name = channel['itunes:owner'][0]['itunes:name'][0];
            }
            if (channel['itunes:owner'][0]['itunes:email']) {
                owner.email = channel['itunes:owner'][0]['itunes:email'][0];
            }
            if (channel['itunes:image']) {
                const hasImageHref = (channel['itunes:image'][0] &&
                    channel['itunes:image'][0].$ &&
                    channel['itunes:image'][0].$.href);
                image = hasImageHref ? channel['itunes:image'][0].$.href : null;
            }

            if (image) {
                feed.itunes.image = image;
            }
            feed.itunes.owner = owner;
        }

        copyFromXML(channel, feed.itunes, fields.podcastFeed);
        items.forEach((item, index) => {
            const entry = feed.items[index];
            entry.itunes = {};
            copyFromXML(item, entry.itunes, fields.podcastItem);
            const image = item['itunes:image'];
            if (image && image[0] && image[0].$ && image[0].$.href) {
                entry.itunes.image = image[0].$.href;
            }
        });
    }

    setISODate(item) {
        const date = item.pubDate || item.date;
        if (date) {
            try {
                item.isoDate = new Date(date.trim()).toISOString();
            } catch (e) {
                // Ignore bad date format
            }
        }
    }
}

export default new RssService();
